import { useState } from 'react';
import { getCompanyInterestApiUrl } from '../utils/environment';
import {
  trackCompanyInterestSubmitted,
  trackCompanyInterestSucceeded,
} from '../utils/analytics';

// status: 'idle' | 'submitting' | 'success' | 'error'
const CompanyInterest = () => {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [useCase, setUseCase] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const clearError = () => {
    if (status === 'error') setStatus('idle');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedCompany = company.trim();
    const trimmedUseCase = useCase.trim();

    if (!isValidEmail(trimmedEmail)) {
      setStatus('error');
      setMessage('Please enter a valid work email address.');
      return;
    }
    if (!trimmedCompany) {
      setStatus('error');
      setMessage('Please tell us your company name.');
      return;
    }
    if (!trimmedUseCase) {
      setStatus('error');
      setMessage('Please tell us a little about how your team would use Solidly Meet.');
      return;
    }

    const apiUrl = getCompanyInterestApiUrl();
    if (!apiUrl) {
      setStatus('error');
      setMessage('This form is not configured yet. Please email team@solidly.ai directly.');
      return;
    }

    setStatus('submitting');
    setMessage('');
    trackCompanyInterestSubmitted('company_interest_section');

    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          company: trimmedCompany,
          useCase: trimmedUseCase,
          source: 'meet.solidly.ai',
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        setStatus('success');
        setMessage(data.message || 'Thanks — we’ll be in touch about rolling Solidly Meet out to your team.');
        trackCompanyInterestSucceeded('company_interest_section');
        setEmail('');
        setCompany('');
        setUseCase('');
      } else {
        setStatus('error');
        setMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Network error. Please try again in a moment.');
    }
  };

  return (
    <section className="company-interest" id="company-interest">
      <div className="company-interest-glow" aria-hidden="true" />
      <div className="section-container company-interest-inner">
        <p className="overline overline--center">FOR TEAMS</p>
        <h2 className="section-title">Get Solidly Meet for your company</h2>
        <p className="section-subtitle">
          Rolling it out across a team? Tell us a bit about your setup and we’ll help you get
          everyone up and running.
        </p>

        {status === 'success' ? (
          <div className="waitlist-success" role="status">
            <span className="waitlist-success-check" aria-hidden="true">
              ✓
            </span>
            <p>{message}</p>
          </div>
        ) : (
          <form className="company-interest-form" onSubmit={handleSubmit} noValidate>
            <input
              type="email"
              className="waitlist-input"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError();
              }}
              aria-label="Work email address"
              autoComplete="email"
              disabled={status === 'submitting'}
            />
            <input
              type="text"
              className="waitlist-input"
              placeholder="Company name"
              value={company}
              onChange={(e) => {
                setCompany(e.target.value);
                clearError();
              }}
              aria-label="Company name"
              autoComplete="organization"
              disabled={status === 'submitting'}
            />
            <textarea
              className="waitlist-input company-interest-textarea"
              placeholder="How would your team use Solidly Meet? (team size, tools, use case)"
              value={useCase}
              onChange={(e) => {
                setUseCase(e.target.value);
                clearError();
              }}
              aria-label="How your team would use Solidly Meet"
              rows={4}
              disabled={status === 'submitting'}
            />
            <button type="submit" className="btn btn-primary" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Sending…' : 'Talk to us →'}
            </button>
          </form>
        )}

        {status === 'error' && (
          <p className="waitlist-message waitlist-message--error" role="alert">
            {message}
          </p>
        )}

        <p className="waitlist-fineprint">
          We’ll only use these details to follow up about a team rollout. Prefer email?{' '}
          <a href="mailto:team@solidly.ai">team@solidly.ai</a>.
        </p>
      </div>
    </section>
  );
};

export default CompanyInterest;
